import { useIntl } from 'react-intl';

import { User } from '~/components/Shared/User/User';
import { Table } from '~/eds/src/modules/Table/Table';
import { TableContextType } from '~/enums/TableContextType';
import { Balance } from '~/features/billing/CreditBalance/types';
import { Uuid } from '~/types/Uuid';

interface Props {
  pageIndex: number;
  totalCount: number;
  transactions: Balance[];
  setPanel: (panelName: string, id?: Uuid) => void;
}

export const CreditsLedger = ({
  totalCount,
  transactions,
  setPanel,
}: Props) => {
  const intl = useIntl();

  const columns = [
    {
      key: 'eventDate',
      cellType: 'datetime',
      title: intl.formatMessage({
        id: 'CreditsLedger.eventDateColumnTitle',
        defaultMessage: 'Event Date',
        description: 'Column title for event date',
      }),
      disableSortBy: true,
      disableResizing: true,
      width: 'm',
      mapCellProps: (transaction: Balance) => ({
        datetime: transaction.eventDate,
      }),
    },
    {
      key: 'addedBy',
      cellType: 'user',
      disableSortBy: true,
      title: intl.formatMessage({
        id: 'CreditsLedger.addedByColumnTitle',
        defaultMessage: 'Added By',
        description: 'Column title for user who added the credit',
      }),
      mapCellProps: (transaction: Balance) => ({
        asyncUser: {
          id: transaction.createdBy,
          render: User,
        },
        mode: 'name',
      }),
    },
    {
      key: 'effectiveDate',
      cellType: 'datetime',
      title: intl.formatMessage({
        id: 'CreditsLedger.effectiveDateColumnTitle',
        defaultMessage: 'Effective Date',
        description: 'Column title for effective date',
      }),
      disableSortBy: true,
      disableResizing: true,
      width: 'm',
      mapCellProps: (transaction: Balance) => ({
        datetime: transaction.effectiveDate,
      }),
    },
    {
      key: 'expirationDate',
      cellType: 'datetime',
      title: intl.formatMessage({
        id: 'CreditsLedger.expirationDateColumnTitle',
        defaultMessage: 'Expiration Date',
        description: 'Column title for expiration date',
      }),
      disableSortBy: true,
      disableResizing: true,
      width: 'm',
      mapCellProps: (transaction: Balance) => ({
        datetime: transaction.expirationDate,
      }),
    },
    {
      key: 'renewalDate',
      cellType: 'datetime',
      title: intl.formatMessage({
        id: 'CreditsLedger.renewalDateColumnTitle',
        defaultMessage: 'Renewal Date',
        description: 'Column title for renewal date',
      }),
      width: 'm',
      disableSortBy: true,
      disableResizing: true,
      mapCellProps: (transaction: Balance) => ({
        datetime: transaction.renewalDate,
      }),
    },
    {
      key: 'entryType',
      cellType: 'text',
      title: intl.formatMessage({
        id: 'CreditsLedger.entryTypeColumnTitle',
        defaultMessage: 'Entry Type',
        description: 'Column title for entry type',
      }),
      width: 'm',
      disableSortBy: true,
      disableResizing: true,
      mapCellProps: (transaction: Balance) => ({
        text: transaction.entryType,
      }),
    },
    {
      key: 'credits',
      cellType: 'number',
      title: intl.formatMessage({
        id: 'CreditsLedger.creditsColumnTitle',
        defaultMessage: 'Credits',
        description: 'Column title for credits amount',
      }),
      width: 'm',
      disableSortBy: true,
      disableResizing: true,
      mapCellProps: (transaction: Balance) => ({
        number: transaction.credits,
      }),
    },
  ];

  return (
    <Table
      rowActions={[
        {
          label: intl.formatMessage({
            id: 'CreditsLedger.editBalanceAction',
            defaultMessage: 'Edit Balance',
            description: 'Row action to edit balance',
          }),
          onClick: async (balance: Balance) => {
            if (balance.renewalDate) {
              setPanel('recurring-credits');
            } else {
              setPanel('one-time-credits', balance.id);
            }
          },
        },
      ]}
      name={TableContextType.CreditLedger}
      data={transactions}
      columns={columns}
      options={{
        enableManageColumns: false,
        enableSelectRows: false,
        enablePagination: false,
        enableExportXlsx: true,
      }}
      totalCount={totalCount}
    />
  );
};
